
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, AreaChart, Area } from "recharts";
import { TrendingUp, Calendar, DollarSign } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay, endOfDay } from "date-fns";

interface OrderTrendsChartProps {
  period?: 'week' | 'month' | 'quarter';
}

const OrderTrendsChart = ({ period = 'month' }: OrderTrendsChartProps) => {
  const { data: trendsData, isLoading } = useQuery({
    queryKey: ['order-trends', period],
    queryFn: async () => {
      const days = period === 'week' ? 7 : period === 'month' ? 30 : 90;
      const startDate = subDays(new Date(), days);
      
      const { data: orders, error } = await supabase
        .from('orders')
        .select('created_at, total_amount, status')
        .gte('created_at', startDate.toISOString())
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Group orders by day
      const trendsMap = new Map();
      
      // Initialize all days in range
      for (let i = 0; i < days; i++) {
        const date = subDays(new Date(), days - 1 - i);
        const dateKey = format(date, 'yyyy-MM-dd');
        trendsMap.set(dateKey, {
          date: format(date, 'MMM dd'),
          orders: 0,
          revenue: 0,
          completedOrders: 0
        });
      }

      // Populate with actual data
      orders.forEach(order => {
        const dateKey = format(new Date(order.created_at), 'yyyy-MM-dd');
        const existing = trendsMap.get(dateKey);
        
        if (existing) {
          existing.orders += 1;
          existing.revenue += Number(order.total_amount);
          if (order.status === 'delivered') {
            existing.completedOrders += 1;
          }
        }
      });

      return Array.from(trendsMap.values());
    },
  });

  const chartConfig = {
    orders: {
      label: "Orders",
      color: "hsl(var(--admin-accent-blue))",
    },
    revenue: {
      label: "Revenue",
      color: "hsl(var(--admin-accent-green))",
    },
    completedOrders: {
      label: "Completed",
      color: "hsl(var(--admin-accent-purple))",
    },
  };

  if (isLoading) {
    return (
      <Card className="admin-card border-admin-border animate-pulse">
        <CardHeader>
          <div className="h-4 bg-admin-muted rounded w-1/3" />
        </CardHeader>
        <CardContent>
          <div className="h-64 bg-admin-muted rounded" />
        </CardContent>
      </Card>
    );
  }

  if (!trendsData) return null;

  const totalOrders = trendsData.reduce((sum, day) => sum + day.orders, 0);
  const totalRevenue = trendsData.reduce((sum, day) => sum + day.revenue, 0);
  const avgDailyOrders = totalOrders / trendsData.length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="admin-card border-admin-border">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <Calendar className="h-4 w-4 text-admin-accent-blue" />
              <div>
                <p className="text-sm text-admin-muted-foreground">Total Orders</p>
                <p className="text-xl font-bold text-admin-foreground">{totalOrders}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="admin-card border-admin-border">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <DollarSign className="h-4 w-4 text-admin-accent-green" />
              <div>
                <p className="text-sm text-admin-muted-foreground">Total Revenue</p>
                <p className="text-xl font-bold text-admin-foreground">${totalRevenue.toFixed(2)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className="admin-card border-admin-border">
          <CardContent className="p-4">
            <div className="flex items-center space-x-2">
              <TrendingUp className="h-4 w-4 text-admin-accent-purple" />
              <div>
                <p className="text-sm text-admin-muted-foreground">Daily Average</p>
                <p className="text-xl font-bold text-admin-foreground">{avgDailyOrders.toFixed(1)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Orders Trend Chart */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Order Volume Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-64">
            <AreaChart data={trendsData}>
              <XAxis dataKey="date" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stroke="hsl(var(--admin-accent-blue))" 
                fill="hsl(var(--admin-accent-blue))" 
                fillOpacity={0.2}
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Revenue Trend Chart */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Revenue Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-64">
            <LineChart data={trendsData}>
              <XAxis dataKey="date" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Line 
                type="monotone" 
                dataKey="revenue" 
                stroke="hsl(var(--admin-accent-green))" 
                strokeWidth={2}
                dot={{ fill: "hsl(var(--admin-accent-green))", strokeWidth: 2 }}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>

      {/* Completion Rate Chart */}
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Order Completion Trends</CardTitle>
        </CardHeader>
        <CardContent>
          <ChartContainer config={chartConfig} className="h-64">
            <AreaChart data={trendsData}>
              <XAxis dataKey="date" />
              <YAxis />
              <ChartTooltip content={<ChartTooltipContent />} />
              <Area 
                type="monotone" 
                dataKey="orders" 
                stroke="hsl(var(--admin-accent-blue))" 
                fill="hsl(var(--admin-accent-blue))" 
                fillOpacity={0.1}
                name="Total Orders"
              />
              <Area 
                type="monotone" 
                dataKey="completedOrders" 
                stroke="hsl(var(--admin-accent-purple))" 
                fill="hsl(var(--admin-accent-purple))" 
                fillOpacity={0.3}
                name="Completed Orders"
              />
            </AreaChart>
          </ChartContainer>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrderTrendsChart;
